import { FC, ReactNode } from 'react';
import classNames from 'classnames';
// Import Styles
import styles from './MarkDownReader.module.scss';

const ICONS: Record<string, ReactNode> = {
    info: (
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path
                fill="currentColor"
                d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm.93 12H7.07V6.93h1.86V12ZM8 5.5A1.1 1.1 0 1 1 8 3.3a1.1 1.1 0 0 1 0 2.2Z"
            />
        </svg>
    ),
    warning: (
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path
                fill="currentColor"
                d="M8.68 1.5a.78.78 0 0 0-1.36 0L.2 13.7a.78.78 0 0 0 .68 1.17h14.24a.78.78 0 0 0 .68-1.17L8.68 1.5ZM8.93 11h-1.86V9.14h1.86V11Zm0-3.14h-1.86V4.5h1.86v3.36Z"
            />
        </svg>
    ),
    error: (
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
            <path
                fill="currentColor"
                d="M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0Zm3.36 10.3-1.06 1.06L8 9.06l-2.3 2.3-1.06-1.06L6.94 8l-2.3-2.3 1.06-1.06L8 6.94l2.3-2.3 1.06 1.06L9.06 8l2.3 2.3Z"
            />
        </svg>
    ),
};

interface Props {
    kind?: string;
    title?: string;
    children?: ReactNode;
    /** The Markdown lines of the block, when the reader marks them for an editor. */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- the HTML attribute react-markdown sets
    'data-sourcepos'?: string;
}

/**
 * Renders the custom "alert" hast node produced by remarkAlerts.
 * kind/title come from directive attributes; children are the already
 * rendered Markdown content, so formatting inside the block just works.
 * The icon is picked automatically from the kind; the title is optional.
 */
const MDAlert: FC<Props> = ({ kind = 'info', title, children, 'data-sourcepos': sourcePos }) => {
    const safeKind = ICONS[kind] ? kind : 'info';

    return (
        <div
            className={classNames(styles.alert, {
                [styles.alertInfo]: safeKind === 'info',
                [styles.alertWarning]: safeKind === 'warning',
                [styles.alertError]: safeKind === 'error',
            })}
            role="note"
            data-sourcepos={sourcePos}
        >
            <div className={styles.alertLeftSide}>{ICONS[safeKind]}</div>
            <div className={styles.alertRightSide}>
                {title && <div className={styles.alertTitle}>{title}</div>}
                <div className={styles.alertBody}>{children}</div>
            </div>
        </div>
    );
};

export default MDAlert;
